import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { ApiError } from '@/api/client'
import { UnsavedChangesDialog } from '@/components/UnsavedChanges'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { listUsers } from '@/features/auth/api'
import { useAuth } from '@/features/auth/store'
import {
  EMPLOYEE_STATUSES,
  EMPLOYMENT_TYPES,
  PAY_FREQUENCIES,
  createEmployee,
  listDepartments,
  listEmployeeLocations,
  listEmployees,
  updateEmployee,
  type EmployeeRead,
} from '@/features/employees/api'
import { useDirtyGuard } from '@/lib/dirtyGuard'
import { toast } from '@/lib/toast'

const SELECT_CLASS =
  'h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

const optionalNumber = (message: string) =>
  z
    .string()
    .trim()
    .refine((v) => v === '' || Number.isFinite(Number(v)), { message })
    .refine((v) => v === '' || Number(v) >= 0, { message: 'Cannot be negative' })

const baseSchema = z.object({
  employee_code: z.string().trim().min(1, 'Enter an employee code').max(50),
  department_id: z.string(),
  position: z.string().trim().min(1, 'Enter a position').max(100),
  manager_id: z.string(),
  location_id: z.string().trim().max(100),
  hire_date: z.string().min(1, 'Pick a hire date'),
  termination_date: z.string(),
  employment_type: z.enum(EMPLOYMENT_TYPES),
  salary: z.string().trim().min(1, 'Enter a salary').refine((v) => Number.isFinite(Number(v)) && Number(v) >= 0, {
    message: 'Enter a non-negative number',
  }),
  pay_frequency: z.enum(PAY_FREQUENCIES),
  tax_id: z.string().trim().max(50),
  bank_account: z.string().trim().max(100),
  emergency_contact_name: z.string().trim().max(200),
  emergency_contact_phone: z.string().trim().max(50),
  status: z.enum(EMPLOYEE_STATUSES),
  sales_target_monthly: optionalNumber('Enter a number'),
})

const createExtra = z.object({
  user_id: z.string().trim().min(1, "Enter the account's user ID"),
})

type FormValues = z.infer<typeof baseSchema> & Partial<z.infer<typeof createExtra>>

/**
 * Create and edit in one dialog, following the `CustomerFormDialog` shape.
 *
 * `user_id` is create-only (see `EmployeeUpdate`'s module note) and, like the customer
 * reassignment picker, can only be a dropdown of real names where the signed-in role holds
 * `users:read` -- HR Manager and Super Admin do, but the matrix's nominal Sales Manager
 * grant on this whole screen does not extend there, so it falls back to a plain id field.
 */
export function EmployeeFormDialog({
  open,
  employee,
  onClose,
}: {
  open: boolean
  employee?: EmployeeRead
  onClose: () => void
}) {
  const isEdit = employee !== undefined
  const can = useAuth((s) => s.can)
  const queryClient = useQueryClient()
  const [formError, setFormError] = useState<string | null>(null)

  const mayListUsers = can('users', 'read')
  const users = useQuery({
    queryKey: ['users', 1],
    queryFn: () => listUsers(1),
    retry: false,
    enabled: open && !isEdit && mayListUsers,
  })
  const departments = useQuery({
    queryKey: ['departments', ''],
    queryFn: () => listDepartments(),
    retry: false,
    enabled: open,
  })
  const managers = useQuery({
    queryKey: ['employees', 'managers'],
    queryFn: () => listEmployees({ status: 'active' }),
    retry: false,
    enabled: open,
  })
  const locations = useQuery({
    queryKey: ['employees', 'locations'],
    queryFn: () => listEmployeeLocations(),
    retry: false,
    enabled: open,
  })

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(isEdit ? baseSchema : baseSchema.merge(createExtra)),
    defaultValues: {
      user_id: '',
      employee_code: employee?.employee_code ?? '',
      department_id: employee?.department_id ?? '',
      position: employee?.position ?? '',
      manager_id: employee?.manager_id ?? '',
      location_id: employee?.location_id ?? '',
      hire_date: employee?.hire_date ?? '',
      termination_date: employee?.termination_date ?? '',
      employment_type: (employee?.employment_type as (typeof EMPLOYMENT_TYPES)[number]) ?? 'full_time',
      salary: employee?.salary ?? '',
      pay_frequency: (employee?.pay_frequency as (typeof PAY_FREQUENCIES)[number]) ?? 'monthly',
      tax_id: employee?.tax_id ?? '',
      bank_account: employee?.bank_account ?? '',
      emergency_contact_name: employee?.emergency_contact?.name ?? '',
      emergency_contact_phone: employee?.emergency_contact?.phone ?? '',
      status: (employee?.status as (typeof EMPLOYEE_STATUSES)[number]) ?? 'active',
      sales_target_monthly: employee?.sales_target_monthly ?? '',
    },
  })

  const guard = useDirtyGuard(open && isDirty)

  const close = () => {
    guard.attempt(() => {
      setFormError(null)
      reset()
      onClose()
    })
  }

  const save = useMutation({
    mutationFn: async (values: FormValues) => {
      const contact =
        values.emergency_contact_name || values.emergency_contact_phone
          ? {
              name: values.emergency_contact_name || undefined,
              phone: values.emergency_contact_phone || undefined,
            }
          : null
      const shared = {
        employee_code: values.employee_code,
        department_id: values.department_id || null,
        position: values.position,
        manager_id: values.manager_id || null,
        location_id: values.location_id || null,
        hire_date: values.hire_date,
        termination_date: values.termination_date || null,
        employment_type: values.employment_type,
        salary: values.salary,
        pay_frequency: values.pay_frequency,
        tax_id: values.tax_id || null,
        bank_account: values.bank_account || null,
        emergency_contact: contact,
        status: values.status,
        sales_target_monthly: values.sales_target_monthly || null,
      }
      if (employee) return updateEmployee(employee.id, shared)
      return createEmployee({ ...shared, user_id: values.user_id! })
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['employees'] })
      void queryClient.invalidateQueries({ queryKey: ['employee', saved.id] })
      toast.success(isEdit ? 'Employee record saved.' : 'Employee record created.')
      reset()
      onClose()
    },
    onError: (error: unknown) => {
      setFormError(
        error instanceof ApiError
          ? error.status === 409
            ? 'An employee with that code already exists.'
            : error.message
          : 'Could not reach the server. Try again.',
      )
    },
  })

  const onSubmit = handleSubmit((values) => {
    setFormError(null)
    return save.mutateAsync(values).catch(() => {
      /* surfaced in formError by onError */
    })
  })

  const managerOptions = (managers.data?.data ?? []).filter((m) => m.id !== employee?.id)
  const locationOptions = locations.data ?? []
  // A site closed since this record was saved is no longer offered, but the record still
  // points at it; keep it selectable rather than silently clearing it on the next save.
  const staleLocationId =
    employee?.location_id && locations.isSuccess && !locationOptions.some((l) => l.id === employee.location_id)
      ? employee.location_id
      : null

  return (
    <>
      <Modal
        open={open}
        onClose={close}
        title={isEdit ? `Edit ${employee.employee_code}` : 'New employee'}
        description={
          isEdit
            ? 'Salary, tax ID and bank details stay hidden here from anyone but HR and the employee themself.'
            : 'HR and Super Admin can set salary and banking details on save; anyone else’s changes to those are dropped by the server.'
        }
        footer={
          <>
            <Button variant="secondary" onClick={close} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" form="employee-form" loading={isSubmitting}>
              {isEdit ? 'Save changes' : 'Create employee'}
            </Button>
          </>
        }
      >
        <form id="employee-form" onSubmit={onSubmit} noValidate className="space-y-4">
          {formError && <Alert tone="error">{formError}</Alert>}

          {!isEdit && (
            <Field
              label="User account"
              error={errors.user_id?.message}
              hint={mayListUsers ? undefined : "Paste the account's user ID -- this role cannot browse the user list."}
            >
              {(props) =>
                mayListUsers ? (
                  <select {...props} {...register('user_id')} className={SELECT_CLASS} disabled={users.isLoading}>
                    <option value="">Select a user…</option>
                    {(users.data?.data ?? []).map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.full_name} ({user.email})
                      </option>
                    ))}
                  </select>
                ) : (
                  <Input {...props} {...register('user_id')} autoComplete="off" />
                )
              }
            </Field>
          )}

          <div className="grid grid-cols-2 gap-4">
            <Field label="Employee code" error={errors.employee_code?.message}>
              {(props) => <Input {...props} {...register('employee_code')} autoComplete="off" />}
            </Field>
            <Field label="Position" error={errors.position?.message}>
              {(props) => <Input {...props} {...register('position')} autoComplete="off" />}
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Department" error={errors.department_id?.message} hint="Optional.">
              {(props) => (
                <select {...props} {...register('department_id')} className={SELECT_CLASS}>
                  <option value="">No department</option>
                  {(departments.data?.data ?? []).map((department) => (
                    <option key={department.id} value={department.id}>
                      {department.name}
                    </option>
                  ))}
                </select>
              )}
            </Field>
            <Field label="Manager" error={errors.manager_id?.message} hint="Optional.">
              {(props) => (
                <select {...props} {...register('manager_id')} className={SELECT_CLASS}>
                  <option value="">No manager</option>
                  {managerOptions.map((manager) => (
                    <option key={manager.id} value={manager.id}>
                      {manager.full_name ?? manager.employee_code}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          </div>

          <Field
            label="Location"
            error={errors.location_id?.message}
            hint="Optional -- the warehouse or store this person is based at. A store's manager earns commission on its till sales."
          >
            {(props) => (
              // Remounted once the options arrive: an uncontrolled select registered before
              // its saved value has an <option> falls back to the first one, and the record
              // would show (and on save, keep) "No location" while it is based somewhere.
              <select
                key={locations.isSuccess ? 'loaded' : 'loading'}
                {...props}
                {...register('location_id')}
                className={SELECT_CLASS}
                disabled={locations.isLoading}
              >
                <option value="">No location</option>
                {staleLocationId && <option value={staleLocationId}>Inactive location</option>}
                {locationOptions.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name} ({location.code})
                  </option>
                ))}
              </select>
            )}
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Hire date" error={errors.hire_date?.message}>
              {(props) => <Input {...props} {...register('hire_date')} type="date" />}
            </Field>
            <Field label="Termination date" error={errors.termination_date?.message} hint="Optional.">
              {(props) => <Input {...props} {...register('termination_date')} type="date" />}
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Employment type" error={errors.employment_type?.message}>
              {(props) => (
                <select {...props} {...register('employment_type')} className={SELECT_CLASS}>
                  {EMPLOYMENT_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type.replace('_', ' ')}
                    </option>
                  ))}
                </select>
              )}
            </Field>
            <Field label="Pay frequency" error={errors.pay_frequency?.message}>
              {(props) => (
                <select {...props} {...register('pay_frequency')} className={SELECT_CLASS}>
                  {PAY_FREQUENCIES.map((freq) => (
                    <option key={freq} value={freq}>
                      {freq}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Salary" error={errors.salary?.message}>
              {(props) => <Input {...props} {...register('salary')} inputMode="decimal" className="tabular-nums" />}
            </Field>
            <Field label="Monthly sales target" error={errors.sales_target_monthly?.message} hint="Optional.">
              {(props) => (
                <Input {...props} {...register('sales_target_monthly')} inputMode="decimal" className="tabular-nums" />
              )}
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Tax ID" error={errors.tax_id?.message} hint="Optional.">
              {(props) => <Input {...props} {...register('tax_id')} autoComplete="off" />}
            </Field>
            <Field label="Bank account" error={errors.bank_account?.message} hint="Optional.">
              {(props) => <Input {...props} {...register('bank_account')} autoComplete="off" />}
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Emergency contact name" error={errors.emergency_contact_name?.message} hint="Optional.">
              {(props) => <Input {...props} {...register('emergency_contact_name')} autoComplete="off" />}
            </Field>
            <Field label="Emergency contact phone" error={errors.emergency_contact_phone?.message} hint="Optional.">
              {(props) => <Input {...props} {...register('emergency_contact_phone')} autoComplete="off" />}
            </Field>
          </div>

          <Field label="Status" error={errors.status?.message}>
            {(props) => (
              <select {...props} {...register('status')} className={SELECT_CLASS}>
                {EMPLOYEE_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {status.replace('_', ' ')}
                  </option>
                ))}
              </select>
            )}
          </Field>
        </form>
      </Modal>

      <UnsavedChangesDialog guard={guard} />
    </>
  )
}
